import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../services/api";

// Defaults — no static fallback images; only DB-configured URLs are used
const DEFAULTS = {
  faviconUrl: "",
  touchIconUrl: "",
  siteName: "NIHSS Virtual Doctoral School",
  shortDescription: "",
  logoUrl: "",
};

/**
 * Fetch public branding config and apply favicon / touch icon / title
 * to the document head at runtime.
 */
export function useBranding() {
  const { data } = usePublicConfig();

  const branding = data?.branding || {};
  const general = data?.general || {};

  const faviconUrl = branding.faviconUrl || DEFAULTS.faviconUrl;
  const touchIconUrl = branding.touchIconUrl || DEFAULTS.touchIconUrl;
  const siteName =
    branding.siteName || general.siteName || DEFAULTS.siteName;
  const shortDescription =
    branding.shortDescription ||
    general.siteDescription ||
    DEFAULTS.shortDescription;
  const logoUrl = branding.logoUrl || DEFAULTS.logoUrl;

  // Update <link rel="icon">
  useEffect(() => {
    if (!faviconUrl) return;
    const el =
      document.querySelector('link[rel="icon"]') ||
      document.querySelector('link[rel="shortcut icon"]');
    if (el) el.href = faviconUrl;
  }, [faviconUrl]);

  // Update <link rel="apple-touch-icon">
  useEffect(() => {
    if (!touchIconUrl) return;
    const el = document.querySelector('link[rel="apple-touch-icon"]');
    if (el) el.href = touchIconUrl;
  }, [touchIconUrl]);

  // Update <title>
  useEffect(() => {
    if (siteName) document.title = siteName;
  }, [siteName]);

  // Update <meta name="description">
  useEffect(() => {
    const el = document.querySelector('meta[name="description"]');
    if (el && shortDescription) el.setAttribute("content", shortDescription);
  }, [shortDescription]);

  return { faviconUrl, touchIconUrl, siteName, shortDescription, logoUrl };
}

export function usePublicConfig() {
  return useQuery({
    queryKey: ["public-config"],
    queryFn: async () => {
      const res = await get("/api/v1/users/public-config");
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // cache 5 min
    retry: 1,
  });
}
